import { Form as FormikForm, Formik, FormikErrors, FormikProps, FormikTouched } from 'formik';
import { useEffect, useState } from 'react';
import { Col, Row, Stack } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { BackButton } from '../../../../../components/buttons/BackButton';
import { SimpleButton } from '../../../../../components/buttons/SimpleButton';
import { Forbidden } from '../../../../../components/error/Forbidden';
import { useToast } from '../../../../../context/ToastContext';
import {
  useCreateOrganizationMutation,
  useGetOrganizationQuery,
  useUpdateOrganizationMutation
} from '../../../../../services/endpoints/places/organization';
import { OrganizationContact, SFCOrganization } from '../../../../../types/place.types';
import { phoneRegExp } from '../../../../../utils/consts';
import { getCurrentUser } from '../../../../../services/helper';
import {
  getErrorMessage,
} from '../../../../../utils/utils';
import { getFormUpdateWithIdArrays } from '../../../../../utils/formDiffUtils';
import { canViewOrganizations } from '../../../../../utils/rbac';
import { useDataContext } from '../../../../../context';
import { FadeIn } from '../../../../animations/FadeIn';
import { OrganizationForm } from './OrganizationForm';

export interface FormValues {
  id?: number;
  name: string;
  location_id: { value: number; label: string };
  venue_ids: { value: number; label: string }[];
  notes: string;
  contacts: OrganizationContact[];
}

type Overwrite<T, NewT> = Omit<T, keyof NewT> & NewT;

export type CreateFormValues = Overwrite<
    FormValues,
    {
      venue_ids?: number[];
      location_id: number;
    }
>;

export interface StepProps {
  errors: Partial<FormikErrors<FormValues>>;
  touched: Partial<FormikTouched<FormValues>>;
  values?: FormValues;
  setFieldValue?: (field: string, value: any, shouldValidate?: boolean) => void;
  readOnly?: boolean;
}

export const ContactSchema = Yup.object({
  first_name: Yup.string().required('First Name is required'),
  last_name: Yup.string().required('Last Name is required'),
  title: Yup.string().optional(),
  phone_number: Yup.string()
      .matches(phoneRegExp, 'Phone number is not valid')
      .min(10, 'Phone number should have at least 10 digits')
      .optional(),
  email: Yup.string().email('Email is invalid').required('Email is required'),
  should_be_sent_roster: Yup.boolean().required().default(false),
  should_be_sent_invoices: Yup.boolean().required().default(false),
  should_be_sent_bulk_welcomes: Yup.boolean().required().default(false),
  should_be_sent_bulk_thanks: Yup.boolean().required().default(false)
});

const validationSchemas = [
  Yup.object({
    name: Yup.string().required('Title is required'),
    location_id: Yup.object({
      value: Yup.number().min(1, 'Location is required').required('Location is required'),
      label: Yup.string().optional()
    }).required('Location is required'),
    venue_ids: Yup.array()
        .of(Yup.object({ value: Yup.number().positive().required(), label: Yup.string().optional() }))
        .optional(),
    notes: Yup.string().optional(),
    contacts: Yup.array().of(ContactSchema).optional()
  })
];

const toOrganizationPayload = (formValue: FormValues): CreateFormValues => {
  const payload: CreateFormValues = {
    name: formValue.name,
    location_id: formValue.location_id.value,
    notes: formValue.notes ?? '',
    venue_ids: formValue.venue_ids.map((o) => o.value),
    contacts: formValue.contacts.map(({ full_name, ...contact }) => contact),
  };

  return payload;
};

const toOrganizationBaseline = (organization: SFCOrganization): CreateFormValues => ({
  name: organization.name,
  location_id: organization.location?.id ?? organization.location_id,
  notes: organization.notes ?? '',
  venue_ids: organization.venues?.map((o) => o.id) ?? [],
  contacts: organization.contacts ?? [],
});

interface AddEditOrganizationProps {
  editing?: boolean;
}

export const AddEditOrganization: React.FC<AddEditOrganizationProps> = ({ editing }) => {
  const params = useParams();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const currentUser = getCurrentUser();
  const { getLocationTitle } = useDataContext();

  const [createOrganization] = useCreateOrganizationMutation();
  const [updateOrganization] = useUpdateOrganizationMutation();
  const { data, error, isLoading } = useGetOrganizationQuery(Number(params.id!), {
    skip: !editing || !params.id
  });

  const [initialValues, setInitialValues] = useState<FormValues>({
    name: '',
    location_id: { value: 0, label: '' },
    venue_ids: [],
    notes: '',
    contacts: []
  });

  useEffect(() => {
    if (!editing && currentUser && !currentUser.isGlobal && currentUser.location_id) {
      setInitialValues((prev) => ({
        ...prev,
        location_id: {
          value: currentUser.location_id,
          label: getLocationTitle(currentUser.location_id)
        }
      }));
    }
  }, [editing, currentUser, getLocationTitle]);

  useEffect(() => {
    if (editing && error && !isLoading) {
      addToast('Error while loading session', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, error, isLoading]);

  useEffect(() => {
    if (data && data.organization) {
      const { organization } = data;
      let values = {
        ...organization,
        ...{ venue_ids: organization.venues?.map((o) => ({ value: o.id, label: o.title })) ?? [] },
        ...{
          location_id: {
            value: organization?.location?.id ?? 0,
            label: organization?.location?.title ?? ''
          }
        }
      };
      setInitialValues(values);
    }
  }, [data]);

  const handleAddOrganization = async (formValue: FormValues) => {
    try {
      let submission = toOrganizationPayload(formValue);
      if (!submission.location_id && currentUser?.location_id && !currentUser.isGlobal) {
        submission.location_id = currentUser.location_id;
      }
      if (!editing && submission.venue_ids?.length === 0) {
        delete submission.venue_ids;
      }

      try {
        if (editing) {
          if (!data?.organization) return;

          const baseline = toOrganizationBaseline(data.organization);
          let update = getFormUpdateWithIdArrays(baseline, submission, ['venue_ids']);
          let submitUpdate = Object.keys(update).length > 0;
          if (submitUpdate) {
            update.id = data.organization.id;
            const res = await updateOrganization(update as CreateFormValues).unwrap();
            if (res && res.organization) {
              addToast(`Organization updated succesfully`, 'success');
            }
          } else {
            addToast(`No changes to Organization`, 'info');
          }
          navigate(`/admin/places/organizations/${data?.organization.id}`);
        } else {
          const res = await createOrganization(submission).unwrap();
          if (res && res.organization) {
            navigate(`/admin/places/organizations/${res.organization.id}`);
            addToast('Organization created succesfully', 'success');
          } else throw new Error('A problem happened while creating Organization');
        }
      } catch (e) {
        addToast(getErrorMessage(e), 'error');
      }
    } catch (error: any) {
      console.log(error);
    }
  };

  // RBAC: Admin, Location Admin, Global Admin, Super Admin, Supreme Admin
  if (!canViewOrganizations(currentUser)) {
    return <Forbidden />;
  }

  return (
      <FadeIn className="p-lg-4">
        <Stack
            gap={3}
            className="py-lg-0 px-4 py-2"
        >
          <div>
            <BackButton
                text={`Back to organization${editing ? ' details' : 's'}`}
                color="#2B4E64"
                url={
                  editing
                      ? `/admin/places/organizations/${data?.organization.id}`
                      : '/admin/places/organizations'
                }
            />
            <h2
                className="d-none d-lg-block text-info fw-bold mb-0"
                style={{ fontSize: '2.25rem' }}
            >
              {editing ? 'Edit' : 'New'} Organization
            </h2>
          </div>

          <div
              className="p-lg-5 p-3"
              style={{
                boxShadow: '0 4px 8px -2px rgba(16, 24, 40, .1),  0 4px 4px 0 rgba(0, 0, 0, .25)',
                border: '1px solid #EBEBEB',
                borderRadius: 10
              }}
          >
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchemas[0]}
                onSubmit={handleAddOrganization}
                enableReinitialize
            >
              {({
                  submitForm,
                  isSubmitting,
                  validateForm,
                  setTouched,
                  touched,
                  errors,
                  values,
                  setFieldValue
                }: FormikProps<FormValues>) => {
                const handleSubmitClick = async () => {
                  const validationErrors = await validateForm();
                  if (Object.keys(validationErrors).length > 0) {
                    setTouched({
                      name: true,
                      location_id: { value: true, label: true },
                      venue_ids: values.venue_ids.map(() => ({ value: true, label: true })),
                      notes: true,
                      contacts: values.contacts.map(() => ({
                        first_name: true,
                        last_name: true,
                        email: true,
                        title: true,
                        phone_number: true
                      }))
                    });
                    addToast('Please fix the errors in the form', 'error');
                    return;
                  }
                  submitForm();
                };

                return (
                    <FormikForm className="text-start">
                      <div style={{ minHeight: '65vh' }}>
                        <OrganizationForm
                            errors={errors}
                            touched={touched}
                            values={values}
                            setFieldValue={setFieldValue}
                        />
                      </div>

                      <Row className="justify-content-end mt-3">
                        <Col
                            xs={6}
                            lg={3}
                        >
                          <SimpleButton
                              disabled={isSubmitting}
                              type="button"
                              onClick={handleSubmitClick}
                              className="w-100"
                              variant="primary"
                          >
                            {editing ? 'Save' : 'Create'}
                          </SimpleButton>
                        </Col>
                      </Row>
                    </FormikForm>
                );
              }}
            </Formik>
          </div>
        </Stack>
      </FadeIn>
  );
};